import { useEffect, useState, type FormEvent } from 'react';
import { useInvestmentRanges } from '../hooks/useInvestmentRanges';
import { useTurnstile } from '../hooks/useTurnstile';
import type { QualifyRequest } from '../lib/api/types';
import type { InvestmentRangeSlug } from '../lib/routing/types';
import { getTrackingParams } from '../lib/tracking';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as
  | string
  | undefined;

interface Props {
  onSubmit: (data: QualifyRequest) => void;
  submitting: boolean;
}

interface FieldErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  investmentRange?: string;
  turnstile?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9\-().\s]{7,}$/;

export function QualifyForm({ onSubmit, submitting }: Props) {
  const ranges = useInvestmentRanges();
  const turnstile = useTurnstile(TURNSTILE_SITE_KEY);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [investmentRange, setInvestmentRange] = useState<
    InvestmentRangeSlug | ''
  >('');
  const [website, setWebsite] = useState(''); // honeypot
  const [errors, setErrors] = useState<FieldErrors>({});

  // Capture utm_* / kuid / kref from the landing URL as soon as the
  // widget mounts — getTrackingParams() persists them to
  // sessionStorage so they survive until submit.
  useEffect(() => {
    getTrackingParams();
  }, []);

  function validate(): FieldErrors {
    const e: FieldErrors = {};
    if (!firstName.trim()) e.firstName = 'Required';
    if (!lastName.trim()) e.lastName = 'Required';
    if (!email.trim()) e.email = 'Required';
    else if (!EMAIL_RE.test(email)) e.email = 'Enter a valid email';
    if (!phone.trim()) e.phone = 'Required';
    else if (!PHONE_RE.test(phone)) e.phone = 'Enter a valid phone';
    if (!investmentRange) e.investmentRange = 'Choose one';
    if (TURNSTILE_SITE_KEY && !turnstile.token) {
      e.turnstile = turnstile.ready
        ? 'Please complete the verification check'
        : 'Verification is loading — try again in a moment';
    }
    return e;
  }

  function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    onSubmit({
      ...getTrackingParams(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      investmentRange: investmentRange as InvestmentRangeSlug,
      website,
      turnstileToken: turnstile.token ?? undefined,
    });
  }

  return (
    <form className="qualify-form" onSubmit={handleSubmit} noValidate>
      <div className="form-row">
        <div className="form-field">
          <label htmlFor="firstName">First name</label>
          <input
            id="firstName"
            name="firstName"
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={submitting}
            maxLength={50}
            aria-invalid={!!errors.firstName}
          />
          {errors.firstName && (
            <span className="field-error">{errors.firstName}</span>
          )}
        </div>
        <div className="form-field">
          <label htmlFor="lastName">Last name</label>
          <input
            id="lastName"
            name="lastName"
            type="text"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={submitting}
            maxLength={50}
            aria-invalid={!!errors.lastName}
          />
          {errors.lastName && (
            <span className="field-error">{errors.lastName}</span>
          )}
        </div>
      </div>

      <div className="form-field">
        <label htmlFor="email">Email</label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting}
          aria-invalid={!!errors.email}
        />
        {errors.email && <span className="field-error">{errors.email}</span>}
      </div>

      <div className="form-field">
        <label htmlFor="phone">Phone</label>
        <input
          id="phone"
          name="phone"
          type="tel"
          autoComplete="tel"
          inputMode="tel"
          placeholder="(555) 123-4567"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={submitting}
          aria-invalid={!!errors.phone}
        />
        {errors.phone && <span className="field-error">{errors.phone}</span>}
      </div>

      <div className="form-field">
        <label htmlFor="investmentRange">Investment amount</label>
        <select
          id="investmentRange"
          name="investmentRange"
          value={investmentRange}
          onChange={(e) =>
            setInvestmentRange(e.target.value as InvestmentRangeSlug | '')
          }
          disabled={submitting || ranges.status !== 'ok'}
          aria-invalid={!!errors.investmentRange}
        >
          <option value="">
            {ranges.status === 'loading' && 'Loading…'}
            {ranges.status === 'error' && 'Could not load options'}
            {ranges.status === 'ok' && 'Select an amount'}
          </option>
          {ranges.status === 'ok' &&
            ranges.ranges.map((r) => (
              <option key={r.slug} value={r.slug}>
                {r.display_label}
              </option>
            ))}
        </select>
        {errors.investmentRange && (
          <span className="field-error">{errors.investmentRange}</span>
        )}
      </div>

      {/* Honeypot — hidden from humans, fillable by bots */}
      <div className="honeypot" aria-hidden="true">
        <label htmlFor="website">Website (leave blank)</label>
        <input
          id="website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      {TURNSTILE_SITE_KEY && (
        <div className="turnstile-wrap">
          <div ref={turnstile.containerRef} className="turnstile-widget" />
          {errors.turnstile && (
            <span className="field-error turnstile-error">
              {errors.turnstile}
            </span>
          )}
        </div>
      )}

      <button
        type="submit"
        className="submit-btn"
        disabled={
          submitting || (TURNSTILE_SITE_KEY != null && !turnstile.token)
        }
      >
        {submitting
          ? 'Finding your advisor…'
          : TURNSTILE_SITE_KEY && !turnstile.token
            ? turnstile.ready
              ? 'Verifying you’re human…'
              : 'Loading verification…'
            : 'Find my advisor'}
      </button>

      <p className="form-disclaimer">
        We respect your privacy. Your information is used only to schedule
        your meeting and is never sold.
      </p>
    </form>
  );
}
